import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { ItpHockeyCanadaPrecedentsBoard, SportDisputePrecedentsBoard } from '@api/BoardSDK';
import { STAFF, hasPerm, logActivity, requireSession } from './auth.server';
import { allowedLibs, assertVisible, fetchOne, fetchPage, redact, LISTED_GROUP, REVIEW_GROUP, REVIEW_ROLES, type DecisionQuery } from './decisions.server';
import type { Decision } from '@generated/lib/decision-utils';
import type { LibKey } from '@generated/models';

const libZ = z.enum(['itp', 'sd']);
const filtersZ = z.object({
  search: z.string().max(120).optional(),
  document: z.string().optional(),
  sport: z.string().optional(),
  province: z.string().optional(),
  level: z.string().optional(),
  finding: z.string().optional(),
  complaintType: z.string().optional(),
  year: z.string().optional(),
});

const cursorsZ = z.object({ itp: z.string().nullable().optional(), sd: z.string().nullable().optional() }).optional();

async function pageAcross(libs: LibKey[], q: DecisionQuery, cursors: { itp?: string | null; sd?: string | null } | undefined) {
  const results = await Promise.all(
    libs.map(async (lib) => {
      const c = cursors?.[lib];
      if (cursors && c === null) return { lib, items: [] as Decision[], cursor: null };
      const r = await fetchPage(lib, q, 20, c ?? undefined);
      return { lib, ...r };
    }),
  );
  const next: { itp: string | null; sd: string | null } = { itp: null, sd: null };
  results.forEach((r) => (next[r.lib] = r.cursor));
  return { items: results.flatMap((r) => r.items), cursors: next, hasMore: !!(next.itp || next.sd) };
}

export const listDecisions = createServerFn({ method: 'POST' })
  .validator(z.object({ sid: z.string(), lib: z.enum(['all', 'itp', 'sd']), filters: filtersZ, cursors: cursorsZ }))
  .handler(async ({ data }) => {
    const user = await requireSession(data.sid);
    const libs = allowedLibs(user, data.lib);
    const res = await pageAcross(libs, { ...data.filters, published: 'any', groups: [LISTED_GROUP] }, data.cursors);
    return { ...res, items: res.items.map((d) => redact(d, user)) };
  });

export const listForReview = createServerFn({ method: 'POST' })
  .validator(z.object({ sid: z.string(), lib: z.enum(['all', 'itp', 'sd']), search: z.string().max(120).optional(), cursors: cursorsZ }))
  .handler(async ({ data }) => {
    const user = await requireSession(data.sid, [...REVIEW_ROLES]);
    const libs = allowedLibs(user, data.lib);
    const res = await pageAcross(libs, { search: data.search, published: 'any', groups: [REVIEW_GROUP] }, data.cursors);
    const counts = await Promise.all(
      libs.map((lib) =>
        (lib === 'itp' ? new ItpHockeyCanadaPrecedentsBoard() : new SportDisputePrecedentsBoard())
          .aggregate()
          .where({ group: [REVIEW_GROUP] })
          .countItems('total')
          .execute()
          .then((r) => r?.[0]?.total ?? 0)
          .catch(() => 0),
      ),
    );
    return { ...res, items: res.items.map((d) => redact(d, user)), pending: counts.reduce((a, b) => a + b, 0) };
  });

const OPTION_COLS = ['document', 'sport', 'province', 'level', 'finding', 'complaintType', 'process', 'anonymousComplainant', 'respondentsAge', 'respondentsAgeBracket', 'jurisdiction', 'keywords', 'mitigatingFactors', 'aggravatingFactors', 'sanctions', 'substantiatedIssues', 'unsubstantiatedIssues', 'respondentsRole', 'respondentsGender', 'severityWarningLongTermSanction'] as const;

async function groupLabels(lib: LibKey, col: string): Promise<string[]> {
  try {
    const board = lib === 'itp' ? new ItpHockeyCanadaPrecedentsBoard() : new SportDisputePrecedentsBoard();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows = (await (board.aggregate() as any).groupBy(col).countItems('n').execute()) as Record<string, unknown>[];
    const out = new Set<string>();
    rows?.forEach((r) => {
      const v = r[col];
      const vals = Array.isArray(v) ? v : typeof v === 'string' ? v.split(/,\s*/) : [];
      vals.forEach((x) => typeof x === 'string' && x.trim() && out.add(x.trim()));
    });
    return [...out].sort((a, b) => a.localeCompare(b));
  } catch {
    return [];
  }
}

export const getOptions = createServerFn({ method: 'POST' })
  .validator(z.object({ sid: z.string(), lib: z.enum(['all', 'itp', 'sd']) }))
  .handler(async ({ data }) => {
    const user = await requireSession(data.sid);
    const libs = allowedLibs(user, data.lib);
    const result: Record<string, string[]> = {};
    await Promise.all(
      OPTION_COLS.map(async (col) => {
        const lists = await Promise.all(libs.map((l) => groupLabels(l, col)));
        result[col] = [...new Set(lists.flat())].sort((a, b) => a.localeCompare(b));
      }),
    );
    return { options: result };
  });

export const getDecision = createServerFn({ method: 'POST' })
  .validator(z.object({ sid: z.string(), lib: libZ, id: z.string() }))
  .handler(async ({ data }) => {
    const user = await requireSession(data.sid);
    if (!user.libraries.includes(data.lib)) throw new Error('You do not have access to this library.');
    const { decision, fileName } = await fetchOne(data.lib, data.id);
    assertVisible(decision.group, user.role);

    return { decision: redact(decision, user), fileName };
  });

export const getDecisionFile = createServerFn({ method: 'POST' })
  .validator(z.object({ sid: z.string(), lib: libZ, id: z.string() }))
  .handler(async ({ data }) => {
    const user = await requireSession(data.sid);
    if (!user.libraries.includes(data.lib)) throw new Error('You do not have access to this library.');
    if (!hasPerm(user, 'view_full_decisions') && !STAFF.includes(user.role)) throw new Error('Your account cannot open full decisions.');
    const { decision, fileUrl } = await fetchOne(data.lib, data.id);
    assertVisible(decision.group, user.role);
    if (!fileUrl) throw new Error('No published decision file is attached.');
    const resp = await fetch(fileUrl);
    if (!resp.ok) throw new Error('The decision file could not be loaded.');
    const bytes = new Uint8Array(await resp.arrayBuffer());
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    const buf = { toString: (_e: 'base64') => btoa(bin) };
    await logActivity(user.username, 'View Decision', 'Opened protected viewer', decision.name);
    return { base64: buf.toString('base64'), watermark: `${user.fullName} — ${user.organization || 'Sport Decisions Library'} — Accessed ${new Date().toISOString().slice(0, 10)}` };
  });

const patchZ = z.object({
  document: z.string().optional(), sport: z.string().optional(), process: z.string().optional(), adjudicator: z.string().optional(),
  level: z.string().optional(), province: z.string().optional(), jurisdiction: z.array(z.string()).optional(), date: z.string().nullable().optional(),
  complaintType: z.array(z.string()).optional(), summary: z.string().optional(), keywords: z.array(z.string()).optional(),
  finding: z.array(z.string()).optional(), mitigating: z.array(z.string()).optional(), aggravating: z.array(z.string()).optional(),
  sanctions: z.array(z.string()).optional(), suspensionDuration: z.string().optional(), educationalRequirement: z.string().optional(),
  severityWarning: z.string().optional(), substantiated: z.array(z.string()).optional(), unsubstantiated: z.array(z.string()).optional(),
  notes: z.string().optional(), respondentName: z.string().optional(), respondentAge: z.string().optional(),
  respondentRole: z.array(z.string()).optional(), respondentGender: z.array(z.string()).optional(), anonymous: z.string().optional(),
});

export const saveDecision = createServerFn({ method: 'POST' })
  .validator(z.object({ sid: z.string(), lib: libZ, id: z.string(), patch: patchZ }))
  .handler(async ({ data }) => {
    const user = await requireSession(data.sid, STAFF);
    const p = data.patch;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const u: Record<string, any> = {};
    const status = (k: string, v: string | undefined) => { if (v) u[k] = v; };
    const set = (k: string, v: unknown) => { if (v !== undefined) u[k] = v; };
    status('document', p.document); status('sport', p.sport); status('level', p.level); status('province', p.province);
    status('anonymousComplainant', p.anonymous);
    set('adjudicator', p.adjudicator); set('jurisdiction', p.jurisdiction); set('complaintType', p.complaintType);
    set('summary', p.summary); set('keywords', p.keywords); set('finding', p.finding); set('mitigatingFactors', p.mitigating);
    set('aggravatingFactors', p.aggravating); set('sanctions', p.sanctions); set('suspensionDuration', p.suspensionDuration);
    set('educationalRequirement', p.educationalRequirement); set('notes', p.notes);
    if (p.date !== undefined) u.dateOfDecision = p.date ? new Date(p.date) : null;
    if (data.lib === 'itp') {
      status('severityWarningLongTermSanction', p.severityWarning); status('respondentsAge', p.respondentAge);
      set('substantiatedIssues', p.substantiated); set('unsubstantiatedIssues', p.unsubstantiated);
      set('respondentsRole', p.respondentRole); set('respondentsGender', p.respondentGender);
      if (hasPerm(user, 'respondent_names')) set('respondentsName', p.respondentName);
      await new ItpHockeyCanadaPrecedentsBoard().item(data.id).update(u).execute();
    } else {
      status('process', p.process); status('respondentsAgeBracket', p.respondentAge);
      if (hasPerm(user, 'respondent_names')) set('respondent', p.respondentName);
      await new SportDisputePrecedentsBoard().item(data.id).update(u).execute();
    }
    await logActivity(user.username, 'Decision Edit', `Updated: ${Object.keys(u).join(', ')}`, data.id);
    const { decision } = await fetchOne(data.lib, data.id);
    return { decision: redact(decision, user) };
  });

export const uploadPublished = createServerFn({ method: 'POST' })
  .validator((d: FormData) => d)
  .handler(async ({ data }) => {
    const sid = String(data.get('sid') ?? '');
    const lib = String(data.get('lib') ?? '') as LibKey;
    const id = String(data.get('id') ?? '');
    const file = data.get('file') as File | null;
    const user = await requireSession(sid, STAFF);
    if (!file || !id || (lib !== 'itp' && lib !== 'sd')) throw new Error('Missing file.');
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) throw new Error('Only PDF files can be published.');
    if (lib === 'itp') {
      await new ItpHockeyCanadaPrecedentsBoard().item(id).uploadFile({ columnId: 'file_mm88bb0w' as 'publishedDecision', file }).execute();
    } else {
      await new SportDisputePrecedentsBoard().item(id).uploadFile({ columnId: 'file_mm7tap93' as 'publishedDecision', file }).execute();
    }
    await logActivity(user.username, 'Publish', `Uploaded ${file.name}`, id);
    return { ok: true };
  });
